import { API, ApiPrefixes } from "../api";

export const memberLoanApi = API.injectEndpoints({
  endpoints: (builder) => ({
    getPendingMemberLoanRequests: builder.query({
      query: ({ page, member_id }) => ({
        url:
          ApiPrefixes["membersLoan"] +
          `/pending/${member_id}?guard=member&page=${page}&limit=20`,
      }),
      providesTags: (result, error, arg) => [
        { type: "member-pending-loans-list", id: arg },
      ],
    }),

    getApprovedMemberLoanList: builder.query({
      query: ({ page, member_id }) => ({
        url:
          ApiPrefixes["membersLoan"] +
          `/approved/${member_id}?guard=member&page=${page}&limit=20`,
      }),
      // providesTags: "approved-loans-list",
      providesTags: (result, error, arg) => [
        { type: "member-approved-loans-list", id: arg },
      ],
    }),

    getDeclinedMemberLoanList: builder.query({
      query: ({ page, member_id }) => ({
        url:
          ApiPrefixes["membersLoan"] +
          `/declined/${member_id}?guard=member&page=${page}&limit=20`,
      }),
      // providesTags: "declined-loans-list",
      providesTags: (result, error, arg) => [
        { type: "member-declined-loans-list", id: arg },
      ],
    }),

    getDisbursedMemberLoanList: builder.query({
      query: ({ page, member_id }) => ({
        url:
          ApiPrefixes["membersLoan"] +
          `/disbursed/${member_id}?guard=member&page=${page}&limit=20`,
      }),
      // providesTags: "disbursed-loans-list",
      providesTags: (result, error, arg) => [
        { type: "member-disbursed-loans-list", id: arg },
      ],
    }),

    getPaidMemberLoanList: builder.query({
      query: ({ page }) => ({
        url:
          ApiPrefixes["membersLoan"] +
          `/requests/paid?guard=member&page=${page}&limit=20`,
      }),
      providesTags: (result, error, arg) => [
        { type: "member-paid-loans-list", id: arg },
      ],
    }),

    createMemberLoanRequest: builder.mutation({
      query: (payload) => ({
        url: ApiPrefixes["membersLoan"] + `/add?guard=member`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [
        { type: "member-pending-loans-list" },
        { type: "member-loan-summary" },
      ],
    }),

    // Named distinctly from admin `loanCalculator` — both inject into the same API
    // and a shared endpoint id would overwrite the member URL with the staff route.
    memberLoanCalculator: builder.mutation({
      query: (payload) => ({
        url: ApiPrefixes["membersLoan"] + `/calculator?guard=member`,
        method: "POST",
        body: payload,
      }),
    }),

    getMemberLoanTypes: builder.query({
      query: (page) => ({
        url: ApiPrefixes["membersLoan"] + `/types?guard=member`,
      }),

      providesTags: (result, error, arg) => [
        { type: "loan-types-list", id: arg },
      ],
    }),

    getMemberBorrowingEligibility: builder.query({
      query: ({ loanTypeId }) => ({
        url:
          ApiPrefixes["membersLoan"] +
          `/borrowing-eligibility?guard=member&loan_type_id=${loanTypeId}`,
      }),
      transformResponse: (responseData) => responseData?.data,
      providesTags: (result, error, arg) => [
        { type: "member-borrowing-eligibility", id: arg?.loanTypeId },
      ],
    }),

    getUpcomingRepaymentLoanList: builder.query({
      query: ({ page, member_id }) => ({
        url:
          ApiPrefixes["membersLoan"] +
          `/repayments/upcoming/${member_id}?guard=member&page=${page}&limit=20`,
      }),
      providesTags: (result, error, arg) => [
        { type: "member-upcoming-repayment-list", id: arg },
      ],
    }),
    getDueRepaymentLoanList: builder.query({
      query: ({ page, member_id }) => ({
        url:
          ApiPrefixes["membersLoan"] +
          `/repayments/due/${member_id}?guard=member&page=${page}&limit=20`,
      }),
      providesTags: (result, error, arg) => [
        { type: "member-due-repayment-list", id: arg },
      ],
    }),
    getOverdueRepaymentLoanList: builder.query({
      query: ({ page, member_id }) => ({
        url:
          ApiPrefixes["membersLoan"] +
          `/repayments/overdue/${member_id}?guard=member&page=${page}&limit=20`,
      }),
      providesTags: (result, error, arg) => [
        { type: "member-overdue-repayment-list", id: arg },
      ],
    }),

    // Get loan request details (for viewing loan information)
    getMemberLoanRequestDetails: builder.query({
      query: (requestId) => ({
        url:
          ApiPrefixes["membersLoan"] +
          `/requests/${requestId}/view?guard=member`,
      }),
      transformResponse: (responseData) => {
        return responseData?.data;
      },
      providesTags: (result, error, arg) => [
        { type: "member-loan-request-details", id: arg },
      ],
    }),

    // Get loan summary (counts and totals for dashboard)
    getMemberLoanSummary: builder.query({
      query: () => ({
        url: ApiPrefixes["membersLoan"] + `/summary?guard=member`,
      }),
      transformResponse: (responseData) => {
        return responseData?.data;
      },
      providesTags: [{ type: "member-loan-summary" }],
    }),

    // Get loan repayment breakdown (repayment schedule)
    getMemberLoanRepaymentBreakdown: builder.query({
      query: (requestId) => ({
        url:
          ApiPrefixes["membersLoan"] +
          `/repayments/breakdown/${requestId}?guard=member`,
      }),
      transformResponse: (responseData) => {
        return responseData?.data;
      },
      providesTags: (result, error, arg) => [
        { type: "member-loan-repayment-breakdown", id: arg },
      ],
    }),

    // Get single repayment details by ID
    getMemberSingleRepayment: builder.query({
      query: (repaymentId) => ({
        url:
          ApiPrefixes["membersLoan"] +
          `/repayments/${repaymentId}/view?guard=member`,
      }),
      transformResponse: (responseData) => {
        return responseData?.data;
      },
      providesTags: (result, error, arg) => [
        { type: "member-single-repayment", id: arg },
      ],
    }),

    // Pay loan repayment using Paystack (for members)
    payMemberLoanRepayment: builder.mutation({
      query: (payload) => ({
        url: ApiPrefixes["membersLoan"] + `/repayments/pay?guard=member`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [
        { type: "member-upcoming-repayment-list" },
        { type: "member-due-repayment-list" },
        { type: "member-overdue-repayment-list" },
        { type: "member-loan-repayment-breakdown" },
        { type: "member-loan-summary" },
      ],
    }),

    // Initiate Paystack payment for informal (flexible) loan repayment (member self-service)
    payMemberInformalLoan: builder.mutation({
      query: (payload) => ({
        url: ApiPrefixes["membersLoan"] + `/repayments/pay-informal?guard=member`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [
        { type: "member-upcoming-repayment-list" },
        { type: "member-due-repayment-list" },
        { type: "member-overdue-repayment-list" },
        { type: "member-loan-repayment-breakdown" },
        { type: "member-loan-summary" },
      ],
    }),

    payMemberLoanRepaymentBankTransfer: builder.mutation({
      query: (payload) => ({
        url: ApiPrefixes["membersLoan"] + `/repayments/pay-bank-transfer?guard=member`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [
        { type: "member-upcoming-repayment-list" },
        { type: "member-due-repayment-list" },
        { type: "member-overdue-repayment-list" },
        { type: "member-loan-repayment-breakdown" },
        { type: "member-loan-summary" },
        { type: "member-bank-transfer-payments" },
        { type: "bank-transfer-payments" },
      ],
    }),

    payMemberInformalLoanBankTransfer: builder.mutation({
      query: (payload) => ({
        url: ApiPrefixes["membersLoan"] + `/repayments/pay-informal-bank-transfer?guard=member`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [
        { type: "member-upcoming-repayment-list" },
        { type: "member-due-repayment-list" },
        { type: "member-overdue-repayment-list" },
        { type: "member-loan-repayment-breakdown" },
        { type: "member-loan-summary" },
        { type: "member-bank-transfer-payments" },
        { type: "bank-transfer-payments" },
      ],
    }),

    // Get payment receipt for a loan repayment
    getMemberLoanRepaymentReceipt: builder.query({
      query: (repaymentId) => ({
        url:
          ApiPrefixes["membersLoan"] +
          `/repayments/${repaymentId}/receipt?guard=member`,
      }),
      transformResponse: (responseData) => {
        return responseData?.data;
      },
      providesTags: (result, error, arg) => [
        { type: "member-loan-repayment-receipt", id: arg },
      ],
    }),

    // Get comprehensive repayment history (overdue, pending, paid) - optimized for profile
    getMemberRepaymentHistory: builder.query({
      query: () => ({
        url: ApiPrefixes["membersLoan"] + `/repayments/history?guard=member`,
      }),
      transformResponse: (responseData) => {
        return responseData?.data;
      },
      providesTags: [{ type: "member-repayment-history" }],
    }),

    getMemberGuarantorRequests: builder.query({
      query: ({ status = "pending", page = 1, limit = 20 } = {}) => ({
        url:
          ApiPrefixes["membersLoan"] +
          `/guarantor-requests?guard=member&status=${status}&page=${page}&limit=${limit}`,
      }),
      transformResponse: (responseData) => responseData?.data,
      providesTags: (result, error, arg) => [
        { type: "member-guarantor-requests", id: arg?.status || "pending" },
        { type: "member-guarantor-requests", id: "LIST" },
      ],
    }),

    getMemberGuarantorRequestsSummary: builder.query({
      query: () => ({
        url:
          ApiPrefixes["membersLoan"] +
          `/guarantor-requests/summary?guard=member`,
      }),
      transformResponse: (responseData) => responseData?.data,
      providesTags: [{ type: "member-guarantor-requests-summary" }],
    }),

    getMemberGuarantorRequestDetails: builder.query({
      query: (id) => ({
        url:
          ApiPrefixes["membersLoan"] +
          `/guarantor-requests/${id}?guard=member`,
      }),
      transformResponse: (responseData) => responseData?.data,
      providesTags: (result, error, id) => [
        { type: "member-guarantor-requests", id },
      ],
    }),

    confirmMemberGuarantorRequest: builder.mutation({
      query: (id) => ({
        url:
          ApiPrefixes["membersLoan"] +
          `/guarantor-requests/${id}/confirm?guard=member`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "member-guarantor-requests" },
        { type: "member-guarantor-requests-summary" },
        { type: "pending-loans-list" },
        { type: "member-pending-loans-list" },
        { type: "loan-request-details" },
        { type: "member-loan-request-details" },
      ],
    }),

    declineMemberGuarantorRequest: builder.mutation({
      query: (id) => ({
        url:
          ApiPrefixes["membersLoan"] +
          `/guarantor-requests/${id}/decline?guard=member`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "member-guarantor-requests" },
        { type: "member-guarantor-requests-summary" },
        { type: "pending-loans-list" },
        { type: "member-pending-loans-list" },
        { type: "loan-request-details" },
        { type: "member-loan-request-details" },
      ],
    }),
  }),
});

export const {
  useGetPendingMemberLoanRequestsQuery,
  useGetApprovedMemberLoanListQuery,
  useGetDeclinedMemberLoanListQuery,
  useGetDisbursedMemberLoanListQuery,
  useGetPaidMemberLoanListQuery,
  useCreateMemberLoanRequestMutation,
  useGetMemberLoanTypesQuery,
  useGetMemberBorrowingEligibilityQuery,
  useMemberLoanCalculatorMutation,
  useGetUpcomingRepaymentLoanListQuery,
  useGetDueRepaymentLoanListQuery,
  useGetOverdueRepaymentLoanListQuery,
  useGetMemberLoanRequestDetailsQuery,
  useGetMemberLoanRepaymentBreakdownQuery,
  useGetMemberSingleRepaymentQuery,
  useGetMemberLoanSummaryQuery,
  usePayMemberLoanRepaymentMutation,
  usePayMemberInformalLoanMutation,
  usePayMemberLoanRepaymentBankTransferMutation,
  usePayMemberInformalLoanBankTransferMutation,
  useGetMemberLoanRepaymentReceiptQuery,
  useGetMemberRepaymentHistoryQuery,
  useGetMemberGuarantorRequestsQuery,
  useGetMemberGuarantorRequestsSummaryQuery,
  useGetMemberGuarantorRequestDetailsQuery,
  useConfirmMemberGuarantorRequestMutation,
  useDeclineMemberGuarantorRequestMutation,
} = memberLoanApi;
